import React, { useState } from 'react';
import { BrowserRouter as Router, Route, Switch, Redirect, useLocation, useHistory } from 'react-router-dom';
import { Layout, Menu, Avatar, Dropdown, Tag } from 'antd';
import {
  ShoppingOutlined,
  CompassOutlined,
  FieldTimeOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  CarOutlined,
  LogoutOutlined,
  UserOutlined,
  CalendarOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import OrdersView       from './components/OrdersView';
import RouteView        from './components/RouteView';
import DriverView       from './components/DriverView';
import MissingCoordsView from './components/MissingCoordsView';
import QueueView        from './components/QueueView';
import Login            from './components/Login';
import ProtectedRoute   from './components/ProtectedRoute';
import UsersView           from './components/UsersView';
import ConfigView          from './components/ConfigView';
import ConfirmationView    from './components/ConfirmationView';
import AuthService         from './services/auth.service';
import { can, PERMISSIONS } from './config/permissions';
import './styles/App.css';

const { Header, Sider, Content } = Layout;

const MENU_ITEMS = [
  { key: '/',              icon: <ShoppingOutlined />,    label: 'Pedidos del Día',   permission: PERMISSIONS.DASHBOARD_ACCESS },
  { key: '/routes',        icon: <CompassOutlined />,     label: 'Rutas Optimizadas', permission: PERMISSIONS.DASHBOARD_ACCESS },
  { key: '/queue',         icon: <FieldTimeOutlined />,   label: 'Cola Dinámica',     permission: PERMISSIONS.DASHBOARD_ACCESS },
  { key: '/missing-coords',icon: <EnvironmentOutlined />, label: 'Sin Coordenadas',   permission: PERMISSIONS.DASHBOARD_ACCESS },
  { key: '/users',         icon: <TeamOutlined />,        label: 'Usuarios',          permission: PERMISSIONS.USERS_READ },
  { key: '/config',        icon: <SettingOutlined />,     label: 'Configuración',     permission: PERMISSIONS.SETTINGS_WRITE },
  { key: '/driver',        icon: <CarOutlined />,         label: 'Vista Motorizado' },
];

const ROLE_COLOR = { SUPERADMIN: 'purple', ADMIN: 'blue', DRIVER: 'green' };
const ROLE_LABEL = { SUPERADMIN: 'Super Admin', ADMIN: 'Admin', DRIVER: 'Driver' };

function AppLayout() {
  const [collapsed, setCollapsed]   = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const location = useLocation();
  const history  = useHistory();

  const currentUser = AuthService.getCurrentUser();
  const userData    = currentUser && currentUser.data;
  const userRole    = userData && userData.role;
  const userName    = userData && (userData.name || userData.email);

  // No autenticado → solo mostrar Login
  if (!AuthService.isAuthenticated()) {
    return (
      <Switch>
        <Route exact path="/login" component={Login} />
        <Redirect to="/login" />
      </Switch>
    );
  }

  const selectedKey = (() => {
    if (location.pathname === '/') return '/';
    const match = MENU_ITEMS.find(i => i.key !== '/' && location.pathname.startsWith(i.key));
    return match ? match.key : '/';
  })();

  const visibleItems = MENU_ITEMS.filter(i => !i.permission || can(userRole, i.permission));

  const userMenu = (
    <Menu>
      <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={() => { AuthService.logout(); window.location.href = '/login'; }}>
        Cerrar Sesión
      </Menu.Item>
    </Menu>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        breakpoint="lg"
        collapsedWidth={window.innerWidth < 992 ? 0 : 80}
        className="app-sider"
      >
        <div className="sider-logo">
          {!collapsed && 'Delivery'}
        </div>

        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
        >
          {visibleItems.map(item => (
            <Menu.Item key={item.key} icon={item.icon} onClick={() => history.push(item.key)}>
              {item.label}
            </Menu.Item>
          ))}
        </Menu>
      </Sider>

      <Layout>
        <Header className="app-header-ant">
          <div className="header-left">
            {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
              className: 'header-trigger',
              onClick: () => setCollapsed(!collapsed),
            })}
          </div>

          <div className="header-right">
            <div className="header-date">
              <CalendarOutlined />
              <input
                type="date"
                value={selectedDate}
                onChange={e => setSelectedDate(e.target.value)}
                className="date-input-ant"
              />
            </div>

            <Dropdown overlay={userMenu} placement="bottomRight" trigger={['click']}>
              <div className="header-user">
                <Avatar icon={<UserOutlined />} size="small" className={`avatar-${(userRole || '').toLowerCase()}`} />
                <span className="header-username">{userName}</span>
                <Tag color={ROLE_COLOR[userRole]} className="header-role-tag">
                  {ROLE_LABEL[userRole] || userRole}
                </Tag>
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content className="app-content-ant">
          <Switch>
            <Route exact path="/login" component={Login} />

            <ProtectedRoute path="/driver">
              <DriverView selectedDate={selectedDate} />
            </ProtectedRoute>

            <ProtectedRoute exact path="/" requiredPermission={PERMISSIONS.DASHBOARD_ACCESS} redirectTo="/driver">
              <OrdersView selectedDate={selectedDate} />
            </ProtectedRoute>

            <ProtectedRoute path="/routes" requiredPermission={PERMISSIONS.DASHBOARD_ACCESS} redirectTo="/driver">
              <RouteView selectedDate={selectedDate} />
            </ProtectedRoute>

            <ProtectedRoute path="/queue" requiredPermission={PERMISSIONS.DASHBOARD_ACCESS} redirectTo="/driver">
              <QueueView selectedDate={selectedDate} />
            </ProtectedRoute>

            <ProtectedRoute path="/missing-coords" requiredPermission={PERMISSIONS.DASHBOARD_ACCESS} redirectTo="/driver">
              <MissingCoordsView />
            </ProtectedRoute>

            <ProtectedRoute path="/users" requiredPermission={PERMISSIONS.USERS_READ} redirectTo="/driver">
              <UsersView />
            </ProtectedRoute>

            <ProtectedRoute path="/config" requiredPermission={PERMISSIONS.SETTINGS_WRITE} redirectTo="/driver">
              <ConfigView />
            </ProtectedRoute>
          </Switch>
        </Content>
      </Layout>
    </Layout>
  );
}

function App() {
  return (
    <Router>
      <Switch>
        {/* Ruta pública — sin autenticación. Accedida desde entregas.mov/{hash} */}
        <Route path="/c/:hash" component={ConfirmationView} />
        {/* Panel admin — requiere autenticación */}
        <Route component={AppLayout} />
      </Switch>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
    </Router>
  );
}

export default App;
